import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Per-tab "any other question" search strip rendered at the bottom of every
 * tab detail page (PRD §C.2 skeleton + §7.1 behaviour).
 *
 * Front-end only in Sprint 1: submits to /:slug/ask?q=… which currently
 * renders a placeholder answer. Sprint 5 wires the answer page to the
 * /api/ask Cloudflare Worker.
 */
export function TabSearchStrip({
  slug,
  topic,
  chips,
}: {
  slug: string;
  topic: string;
  chips?: string[];
}): JSX.Element {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const nav = useNavigate();

  function submit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed.length === 0) return;
    nav(`/${slug}/ask?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <section
      aria-label={`Ask a question about ${topic}`}
      className="border-t border-sovereign-line bg-sovereign-850 px-4 py-6 md:px-8 md:py-8"
    >
      <div className="mx-auto max-w-3xl">
        <form onSubmit={submit} className="relative">
          <label htmlFor={`ask-${slug}`} className="sr-only">
            Ask any other question about {topic}
          </label>
          <input
            id={`ask-${slug}`}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 120)}
            placeholder={`Any other question about ${topic}?`}
            className="w-full rounded-md border border-sovereign-line bg-white px-4 py-3 pr-12 text-sm text-ink shadow-sm outline-none placeholder:text-ink-dim focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/25"
          />
          <button
            type="submit"
            aria-label="Ask"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-brand-purple px-2.5 py-1.5 text-white shadow-card hover:bg-brand-purple-dark"
          >
            <span aria-hidden>➤</span>
          </button>
        </form>

        {focused && chips && chips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {chips.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  nav(`/${slug}/ask?q=${encodeURIComponent(c)}`);
                }}
                className="rounded-full border border-sovereign-line bg-white px-3 py-1 text-xs text-ink-mute hover:border-brand-purple hover:text-brand-purple"
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
